/**
 * The words and the selection rules of a bar chart (ADR-061). `components/BarChart` draws; this
 * says what a tapped bar reads and which bar an arrow key moves to, so both are testable without a
 * DOM. Every chart has a readout line under it: the hint while nothing is selected, the bar's day
 * (or week, or hour) and its exact figure once one is.
 */

/** What one bar stands for — it decides the hint's last word. */
export type ChartPer = "day" | "week" | "hour";

const grouped = (n: number) => Math.round(n).toLocaleString("en-US");

/** "Tap a bar for its day" — always there, so the line never jumps and the tap is discoverable. */
export function chartHint(per: ChartPer): string {
  return `Tap a bar for its ${per}`;
}

/** "Tue 15 Sep · 6,214 steps"; an empty bar says so rather than reading as a zero. */
export function singleReadout(name: string, value: number | null, format: (v: number) => string = grouped, unit?: string): string {
  if (value === null || value <= 0) return `${name} · nothing recorded`;
  return `${name} · ${format(value)}${unit ? ` ${unit}` : ""}`;
}

/**
 * "Tue 15 Sep · In 1,240 · Out ~2,310 kcal"; weekly "Week of 14 Sep · In avg 1,480 · Out ~2,060
 * kcal a day". The unit follows the last figure, so a missing Out never reads "not known kcal".
 */
export function pairedReadout(
  name: string,
  inV: number | null,
  outV: number | null,
  opts: { outEstimated?: boolean; weekly?: boolean } = {}
): string {
  const avg = opts.weekly ? "avg " : "";
  const parts: { text: string; figure: boolean }[] = [
    inV !== null && inV > 0 ? { text: `In ${avg}${grouped(inV)}`, figure: true } : { text: "In not logged", figure: false },
    outV !== null && outV > 0
      ? { text: `Out ${opts.outEstimated ? "~" : ""}${grouped(outV)}`, figure: true }
      : { text: "Out not known", figure: false },
  ];
  const last = parts.map((p) => p.figure).lastIndexOf(true);
  if (last >= 0) parts[last].text += opts.weekly ? " kcal a day" : " kcal";
  return [name, ...parts.map((p) => p.text)].join(" · ");
}

/** "14:00" */
export const hourLabel = (h: number) => `${String(h % 24).padStart(2, "0")}:00`;

/** "14:00–15:00 · 658 steps" */
export function denseReadout(hour: number, value: number | null, unit?: string): string {
  const span = `${hourLabel(hour)}–${hourLabel(hour + 1)}`;
  if (value === null || value <= 0) return `${span} · nothing recorded`;
  return `${span} · ${grouped(value)}${unit ? ` ${unit}` : ""}`;
}

export type ChartKey = "next" | "prev" | "first" | "last";

/**
 * The bar an arrow key (or Home/End) moves to: the nearest one in that direction with something
 * to read, skipping empty bars. From nothing selected, "next" starts at the first and "prev" at
 * the last. At an end it stays where it is; with no bar to read it is null.
 */
export function nextSelectable(present: readonly boolean[], from: number | null, key: ChartKey): number | null {
  const idx = present.map((p, i) => (p ? i : -1)).filter((i) => i >= 0);
  if (!idx.length) return null;
  if (key === "first") return idx[0];
  if (key === "last") return idx[idx.length - 1];
  if (from === null) return key === "next" ? idx[0] : idx[idx.length - 1];
  if (key === "next") return idx.find((i) => i > from) ?? from;
  const back = idx.filter((i) => i < from);
  return back.length ? back[back.length - 1] : from;
}

/**
 * The slot under a tap at `x` (in the chart's own units): slots run from `left` in steps of
 * `slot`. Left of the first or right of the last is null — a tap there clears.
 */
export function slotAt(x: number, left: number, slot: number, n: number): number | null {
  if (slot <= 0 || n <= 0) return null;
  const i = Math.floor((x - left) / slot);
  return i >= 0 && i < n ? i : null;
}
